<script>
  // A single grid tile in the CardCollection shell (ADR-0010). Renders the
  // normalized card-item model with the owner-stack visual.
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import { deriveOwners } from '../../lib/logic/card-owners.js';
  import OwnerAvatars from './OwnerAvatars.svelte';
  import CardPrice from './CardPrice.svelte';

  let { item, currencySymbol = '$', onCardClick = null } = $props();

  const imageUrl = $derived(getCardImageUrl(item.imageRef, 'normal'));
  const owners = $derived(deriveOwners(item.holders));

  function clickTile() {
    if (onCardClick) onCardClick(item);
  }
</script>

<div class="cc-tile">
  <svelte:element
    this={onCardClick ? 'button' : 'div'}
    class="cc-tile-card"
    class:clickable={!!onCardClick}
    type={onCardClick ? 'button' : undefined}
    role={onCardClick ? 'button' : undefined}
    onclick={onCardClick ? clickTile : undefined}
    aria-label={onCardClick ? `View ${item.name}` : undefined}
  >
    <div class="card-image-stack">
      {#if owners.length >= 3}
        <div class="stack-layer stack-3"></div>
      {/if}
      {#if owners.length >= 2}
        <div class="stack-layer stack-2"></div>
      {/if}
      <div class="card-image-wrapper">
        {#if imageUrl}
          <img src={imageUrl} alt={item.name} loading="lazy" width="488" height="680" />
        {/if}
        {#if owners.length > 1}
          <div class="owner-count-badge">{owners.length}</div>
        {/if}
      </div>
    </div>

    <div class="cc-tile-info">
      <div class="cc-tile-footer">
        <OwnerAvatars {owners} max={3} size={18} overlap={5} showSingleName />
        <CardPrice price={item.price} {currencySymbol} isSample={item.isSample} />
      </div>
    </div>
  </svelte:element>
</div>

<style>
  .cc-tile {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .cc-tile-card {
    all: unset;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    transition: transform 0.2s ease;
  }
  .cc-tile-card.clickable {
    cursor: pointer;
  }
  .cc-tile-card.clickable:hover {
    transform: translateY(-4px);
  }

  .card-image-stack {
    position: relative;
  }
  .stack-layer {
    position: absolute;
    border-radius: 0.5rem;
    background: var(--color-accent-bg);
  }
  .stack-layer.stack-3 {
    top: 5px;
    left: 5px;
    right: -5px;
    bottom: -5px;
    background: var(--color-border);
  }
  .stack-layer.stack-2 {
    top: 2.5px;
    left: 2.5px;
    right: -2.5px;
    bottom: -2.5px;
    background: var(--color-accent-bg);
  }

  .card-image-wrapper {
    position: relative;
    border-radius: 0.5rem;
    overflow: hidden;
  }
  .card-image-wrapper img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 0.5rem;
  }

  .owner-count-badge {
    position: absolute;
    top: -7px;
    right: -5px;
    z-index: 10;
    background: var(--color-accent-dark);
    color: var(--color-bg);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(40, 30, 15, 0.25);
    border: 2.5px solid var(--color-bg);
  }

  .cc-tile-info {
    padding: 8px 2px 0;
  }
  .cc-tile-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.25rem;
  }
  @media (max-width: 768px) {
    .cc-tile-card.clickable:hover {
      transform: none;
    }
  }
</style>
